<template>
  <div class="border rounded p-4 mb-4">
    <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
      <div>
        <h5 class="fw-bolder mb-1">Sessões e avisos</h5>
        <div class="text-muted fs-7">
          Acompanhe o andamento das sessões e os avisos enviados ao paciente.
        </div>
      </div>
      <button
        type="button"
        class="btn btn-sm btn-light-primary"
        :disabled="sending"
        @click="sendNotice(false)"
      >
        <i class="bi bi-send me-1"></i>
        {{ sending ? 'Enviando...' : 'Enviar aviso agora' }}
      </button>
    </div>

    <div class="alert alert-warning d-flex align-items-center" v-if="isEnding">
      <i class="bi bi-exclamation-triangle-fill fs-3 me-3"></i>
      <div>
        <span class="fw-bolder d-block">As sessões estão acabando.</span>
        <span class="fs-7">
          {{ remainingText }} até {{ formatDate(summary.last_session_date) }}.
          {{ noticeEnabled
            ? 'O aviso automático é enviado por WhatsApp e e-mail.'
            : 'O aviso automático está desligado neste programa.' }}
        </span>
      </div>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-6 col-md-3">
        <div class="border rounded p-3 h-100">
          <div class="text-muted fs-8 text-uppercase">Realizadas</div>
          <div class="fs-4 fw-bolder">{{ summary.done ?? 0 }} / {{ summary.total ?? 0 }}</div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-3 h-100">
          <div class="text-muted fs-8 text-uppercase">Restantes</div>
          <div class="fs-4 fw-bolder" :class="isEnding ? 'text-warning' : ''">
            {{ summary.remaining ?? 0 }}
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-3 h-100">
          <div class="text-muted fs-8 text-uppercase">Próxima sessão</div>
          <div class="fs-5 fw-bolder">{{ formatDate(summary.next_session_date) }}</div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-3 h-100">
          <div class="text-muted fs-8 text-uppercase">Última agendada</div>
          <div class="fs-5 fw-bolder">{{ formatDate(summary.last_session_date) }}</div>
        </div>
      </div>
    </div>

    <div class="progress h-8px mb-4" v-if="(summary.total ?? 0) > 0">
      <div
        class="progress-bar bg-primary"
        role="progressbar"
        :style="{ width: donePercent + '%' }"
        :aria-valuenow="donePercent"
        aria-valuemin="0"
        aria-valuemax="100"
      ></div>
    </div>

    <div class="table-responsive mb-4" v-if="(summary.by_slot?.length ?? 0) > 1">
      <table class="table table-row-dashed align-middle gs-0 gy-2">
        <thead>
        <tr class="fw-bold text-muted fs-7">
          <th>Profissional / Especialidade</th>
          <th class="text-center">Realizadas</th>
          <th class="text-center">Restantes</th>
          <th class="text-center">Última agendada</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="slot in summary.by_slot" :key="slot.id_sched_program_slot">
          <td>{{ slot.label }}</td>
          <td class="text-center">{{ slot.done }} / {{ slot.total }}</td>
          <td class="text-center fw-bolder">{{ slot.remaining }}</td>
          <td class="text-center">{{ formatDate(slot.last_date) }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <h6 class="fw-bolder mb-2">Avisos enviados</h6>
    <ScheduleProgramMessageTable :program-id="programId" :unique-key="messagesKey" />
  </div>
</template>

<script>
import http from '../../helpers/http';
import Alert from '../../helpers/alert';
import ScheduleProgramMessageTable from './ScheduleProgramMessageTable.vue';

export default {
  name: 'ScheduleProgramSessionsCard',
  components: { ScheduleProgramMessageTable },
  props: {
    programId: { type: [Number, String], required: true },
    summary: { type: Object, default: () => ({}) },
    threshold: { type: [Number, String], default: 2 },
    noticeEnabled: { type: Boolean, default: true },
  },
  emits: ['sent'],
  data() {
    return {
      sending: false,
      messagesKey: 0,
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    isEnding() {
      const remaining = Number(this.summary?.remaining ?? 0);
      return remaining >= 1 && remaining <= Number(this.threshold || 2);
    },
    remainingText() {
      const remaining = Number(this.summary?.remaining ?? 0);
      return remaining === 1 ? 'Resta 1 sessão' : `Restam ${remaining} sessões`;
    },
    donePercent() {
      const total = Number(this.summary?.total ?? 0);
      if (!total) return 0;
      return Math.min(100, Math.round((Number(this.summary?.done ?? 0) / total) * 100));
    },
  },
  methods: {
    date: moment,
    formatDate(value) {
      return value ? this.date(value).format('DD/MM/YYYY') : '-';
    },
    sendNotice(force) {
      if (!this.programId || !this.place?.id_place) return;

      this.sending = true;
      http.post(
        `/places/${this.place.id_place}/schedules/programs/${this.programId}/notify-sessions-ending`,
        { force },
      ).then((response) => {
        if (response.data?.sent) {
          Alert.toastSuccess('Aviso enviado ao paciente.');
        } else {
          Alert.toastInfo('Nenhum canal disponível para o envio. Verifique telefone e e-mail do paciente.');
        }
        this.messagesKey += 1;
        this.$emit('sent');
      }).catch((e) => {
        if (e.response?.status === 422 && !force) {
          this.confirmForce(e.response?.data?.reason);
          return;
        }
        Alert.toastError(e.response?.data?.reason ?? e.response?.data ?? 'Não foi possível enviar o aviso.');
      }).finally(() => {
        this.sending = false;
      });
    },
    confirmForce(reason) {
      Swal.fire({
        title: 'Enviar mesmo assim?',
        html: reason ?? 'O programa não está elegível para o aviso agora.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#780f5a',
        confirmButtonText: 'Sim, enviar',
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.isConfirmed) {
          this.sendNotice(true);
        }
      });
    },
  },
};
</script>
